/**
 * /me/leaves/adjustments — Session 3d PR 6.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { LeaveAdjustmentItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyLeaveAdjustmentsPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-leave-adjustments", employeeId],
    queryFn: () => session3dApi.listMyLeaveAdjustments(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<LeaveAdjustmentItem>[] = [
    {
      headerKey: "me.leave-adjustments.col.date",
      label: "Date",
      render: (r) => <span dir="ltr" className="text-xs">{r.date}</span>,
    },
    {
      headerKey: "me.leave-adjustments.col.type",
      label: "Leave type",
      render: (r) => <span className="font-medium">{r.leave_type_name_en}</span>,
    },
    {
      headerKey: "me.leave-adjustments.col.delta",
      label: "Delta (days)",
      render: (r) => <span className={`font-mono ${r.delta_days >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{r.delta_days >= 0 ? "+" : ""}{r.delta_days.toFixed(1)}</span>,
    },
    {
      headerKey: "me.leave-adjustments.col.reason",
      label: "Reason",
      render: (r) => <span className="text-xs text-text-secondary">{r.reason}</span>,
    },
    {
      headerKey: "me.leave-adjustments.col.actor",
      label: "HR actor",
      render: (r) => <span className="text-[10px] text-text-tertiary">{r.hr_actor_name}</span>,
    },
  ];

  return (
    <MeListPage<LeaveAdjustmentItem>
      title={t("me.leave-adjustments.title", "My Leave Adjustments")}
      subtitle={t("me.leave-adjustments.subtitle", "Read-only audit of HR-initiated balance adjustments.")}
      domain="leave-adjustments"
      khidmatPrefill={t("me.leave-adjustments.ask_khidmat", "Why was my leave balance adjusted?")}
      query={query}
      columns={columns}
      rowKey={(r) => r.event_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.leave-adjustments.title", "My Leave Adjustments"),
        window_label: t("me.leave-adjustments.window", "across the year"),
      }}
      emptyHeadline={t("me.leave-adjustments.empty_headline", "No adjustments to your balances.")}
      emptyBody={t("me.leave-adjustments.empty_body", "Khidmat: when HR makes balance adjustments, they appear here for your records.")}
    />
  );
}
